/**
 * Si hay que avisar antes de cerrar una caja en la que no se cobró nada.
 *
 * Parece una comparación con cero y no lo es, porque las dos cifras que
 * intervienen en el cierre no miden lo mismo:
 *
 * - **Las ventas del turno** son sólo los cobros de sus facturas. No llevan el
 *   fondo de apertura.
 * - **El arqueo** es el conteo físico del cajón, y en efectivo ese conteo sí
 *   incluye el fondo.
 *
 * El guardián preguntaba por las dos a la vez (`ventas === 0 && arqueo === 0`),
 * así que sólo saltaba cuando el fondo de apertura también era cero: abriendo
 * sin importe avisaba, y abriendo con fondo no avisaba, aunque en los dos casos
 * no se hubiera vendido nada. El aviso decía «cerrar la caja sin dinero»
 * mientras que lo que de verdad comprobaba era otra cosa.
 *
 * La pregunta que vale la pena hacer al cerrar es una sola: **si el turno pasó
 * sin una venta**. Que el cajón cuadre con lo que debería tener es un asunto
 * aparte, y de eso se encarga la validación del arqueo contra el margen de la
 * tienda, que corre justo después.
 *
 * @param {number} ventasDelTurno  Suma de los pagos de las facturas del turno.
 * @returns {boolean}
 */
export const hayQueAvisarDeCierreSinVentas = (ventasDelTurno) => {
  const ventas = Number(ventasDelTurno);
  if (!Number.isFinite(ventas)) return true;
  return ventas === 0;
};

/**
 * Por qué el turno cerró en cero: porque no pasó nada, o porque lo que pasó se
 * anuló.
 *
 * La suma que dispara el aviso es **neta**, así que una factura y su nota de
 * crédito por el mismo importe dan cero igual que un turno quieto. Y el aviso
 * decía en los dos casos «no se registró ninguna venta», que en el segundo es
 * **falso**: manda al cajero a buscar facturas sin sincronizar que no existen,
 * cuando lo que hubo fue una venta anulada. Son dos situaciones distintas y
 * piden dos revisiones distintas.
 *
 * @param {number} ventasDelTurno  Suma neta de los pagos del turno.
 * @param {number} movimientos     Cuántas líneas de pago hubo, anuladas o no.
 * @returns {"sinMovimiento"|"anulado"|null} `null` si el turno sí tuvo ventas.
 */
export const motivoDelCierreEnCero = (ventasDelTurno, movimientos = 0) => {
  if (!hayQueAvisarDeCierreSinVentas(ventasDelTurno)) return null;

  return Number(movimientos) > 0 ? "anulado" : "sinMovimiento";
};
